import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { type Schedule } from '@accounts';
import { trackEvent } from '@/shared/analytics';
import { useRecheckSchedules } from '../hooks/useRecheckSchedules';
import { ApplicationStateLabel } from '@accounts';

interface Props {
  accountId: number;
  schedules: Schedule[];
  onFound: (schedules: Schedule[]) => void;
}

export default function NoBookableSchedules({ accountId, schedules, onFound }: Props) {
  const { recheck, isFetching } = useRecheckSchedules(accountId);

  useEffect(() => {
    trackEvent('no_bookable_schedules_shown', { account_id: accountId, count: schedules.length });
  }, [accountId, schedules.length]);

  const handleRecheck = async () => {
    trackEvent('no_bookable_schedules_rechecked', { account_id: accountId });
    onFound(await recheck());
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900">
        <FormattedMessage id="preparation.noBookableSchedules.title" />
      </h3>
      <p className="mt-3 text-sm text-slate-600">
        <FormattedMessage id="preparation.noBookableSchedules.body" />
      </p>

      <ul className="mt-4 space-y-2">
        {schedules.map((schedule) => (
          <li key={schedule.id} className="rounded-xl border border-slate-200 bg-white/70 px-4 py-3">
            <span className="text-sm font-medium text-slate-700">
              #{schedule.external_id ?? schedule.id}
            </span>
            <ApplicationStateLabel schedule={schedule} />
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <button
          type="button"
          onClick={handleRecheck}
          disabled={isFetching}
          className="inline-flex items-center rounded-xl bg-brand-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-brand-500 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
        >
          <FormattedMessage
            id={isFetching ? 'preparation.noSchedules.rechecking' : 'preparation.noSchedules.recheck'}
          />
        </button>
      </div>
    </div>
  );
}
